import { TTranslation } from "@/features/FormCard/hooks";
import { MovementsBySection, DEFAULT_MOVEMENT_MAP } from "./config";
import { MovementType } from "@/data/movementType";

/**
 * Configuration type for section filtering
 */
export type FilterConfig = {
  sectionName: string;
  allowedFields?: string[];
};

export type AccountType = "securities" | "cash" | undefined;

type SectionMap = Record<string, string[]>;

/**
 * Check if account type is securities account (compte titre)
 */
export function isSecuritiesAccount(accountType: string | undefined): boolean {
  return accountType === "securities";
}

/**
 * Check if account type is current account (compte courant)
 */
export function isCashAccount(accountType: string | undefined): boolean {
  return accountType === "cash";
}

export function deriveLocalCurrency(
  accountType?: AccountType,
  portfolioCurrency?: string | null,
  assetCurrency?: string | null,
): string | null {
  if (isCashAccount(accountType)) return portfolioCurrency ?? null;
  if (isSecuritiesAccount(accountType)) return assetCurrency ?? null;
  return null;
}

/**
 * Check if static datum name has CASH xxx format
 */
export function isCashFormat(staticDatumName: string | undefined): boolean {
  if (!staticDatumName) return false;
  return staticDatumName.startsWith("CASH");
}

export function flattenSectionMap(sectionMap: SectionMap): string[] {
  const out = new Set<string>();
  Object.values(sectionMap).forEach((list) => list.forEach((x) => out.add(x)));
  return Array.from(out);
}

export function getAllowedMovements(
  accountType: AccountType,
  assetClass2?: string | null,
  opts?: { isCashAsset?: boolean; marketValueLocalCcy?: number },
): MovementType[] {
  if (!assetClass2 || !accountType) return [];
  if (isSecuritiesAccount(accountType)) {
    let entry: MovementType[] | Record<"pos" | "neg", MovementType[]> =
      DEFAULT_MOVEMENT_MAP.security[assetClass2];
    if (!entry) return [];
    if (Array.isArray(entry)) {
      return entry.slice();
    }
    const sign = (opts?.marketValueLocalCcy ?? 0) > 0 ? "pos" : "neg";
    const bySign = entry[sign] ?? [];
    if (opts?.marketValueLocalCcy == null) {
      const merged = [...(entry.pos ?? []), ...(entry.neg ?? [])];
      return Array.from(new Set(merged));
    }
    return bySign.slice();
  }
  if (isCashAccount(accountType)) {
    if (opts?.isCashAsset && DEFAULT_MOVEMENT_MAP.cash_special_case) {
      const wildcard = DEFAULT_MOVEMENT_MAP.cash_special_case["*"];
      if (wildcard) return Array.from(wildcard);
    }
    let entry = DEFAULT_MOVEMENT_MAP.cash[assetClass2];
    if (!opts?.isCashAsset) {
      // code enters here <=> actif is not cash an account is cash
      // <=> (see FO-1501 including comments) dividend shall be added to movement type
      if (!entry) {
        entry = [];
      }
      if (Array.isArray(entry)) {
        entry.push(MovementType.DIVIDEND);
      }
    }
    return entry ? Array.from(entry) : [];
  }
  return [];
}

/**
 * Get filtered category options based on account type and asset classes
 */
export function getMovementsBySections(
  movementsBySections: MovementsBySection[],
  accountType: AccountType,
  assetClass2: string | null,
  staticDatumName?: string,
  marketValueLocalCcy?: number,
): MovementsBySection[] {
  if (!movementsBySections?.length || !assetClass2 || !accountType) return [];

  // If the rules say "all" for securities, getAllowedMovements will expand it
  // when we pass a default section map (built from the current options).
  const allowedMovements: MovementType[] = getAllowedMovements(
    accountType,
    assetClass2,
    {
      isCashAsset: isCashFormat(staticDatumName),
      marketValueLocalCcy,
    },
  );

  // Nothing allowed → nothing to show
  if (!allowedMovements.length) return [];

  const allowedMovementsSet = new Set(allowedMovements);

  // Keep sections but only with allowed fields; drop empty sections
  return movementsBySections
    .map((movementsBySection) => {
      const filteredMovements = (movementsBySection.movements ?? []).filter(
        (f) => allowedMovementsSet.has(f),
      );
      return { ...movementsBySection, fields: filteredMovements };
    })
    .filter((sections) => sections.fields.length > 0);
}

/**
 * Flattens options from sectioned format to a list of option values
 */
export function extractOptionsValues(options: any[]): string[] {
  if (!options || !Array.isArray(options) || options.length === 0) {
    return [];
  }

  // Handle sectioned options (with items property)
  if ("items" in options[0]) {
    return options.flatMap((section) =>
      section.items ? section.items.map((item: any) => item.label) : [],
    );
  }

  // Handle flat options (with value property)
  if ("value" in options[0]) {
    return options.map((option) => option.label);
  }

  return [];
}

/**
 * Check if should auto-select the category option
 *
 * @param options List of available options
 * @param currentValue Current field value
 * @param isCategoryDisabled Whether the category field is disabled
 * @param translations Optional translations for category values
 * @returns Object with shouldSelect flag and selectedValue if applicable
 */
export function shouldAutoSelectOption(
  options: any[],
  currentValue: string,
  isCategoryDisabled: boolean,
  keysAllowNullValue: string[] = [],
): { shouldSelect: boolean; selectedValue?: string } {
  // Don't auto-select if options are invalid or field is disabled
  if (!options || !Array.isArray(options) || isCategoryDisabled) {
    return { shouldSelect: false };
  }

  const availableOptions = extractOptionsValues(options);

  if (availableOptions.length === 1) {
    const key = availableOptions[0];

    if (currentValue === key || keysAllowNullValue.includes(key)) {
      return { shouldSelect: false };
    }

    return { shouldSelect: true, selectedValue: key };
  }

  return { shouldSelect: false };
}

/**
 * Check if asset class has changed between previous and current value
 *
 * @param prevAssetClass Previous asset class value
 * @param currentAssetClass Current asset class value
 * @returns Whether the asset class has changed and should trigger a reset
 */
export function shouldResetCategoryValue(
  prevAssetClass: string | undefined,
  currentAssetClass: string | undefined,
): boolean {
  return prevAssetClass !== undefined && prevAssetClass !== currentAssetClass;
}

/**
 * Get the key of a translation by its value
 *
 * @param translations Translations object
 * @param value Value to search for
 * @returns Key of the translation if found, undefined otherwise
 */
export function getKeyByTranslation(
  value: string,
  translations?: TTranslation["category"],
): string | undefined {
  return Object.keys(translations ?? {}).find(
    (key) => translations?.[key] === value,
  );
}
